/* ---------------------------------------------------------------------------
   Browser -> Cloudflare R2 uploads via presigned PUT.

   Bytes never touch Convex. XHR rather than fetch because fetch still has no
   upload progress event, and a player staring at a 90MB upload with no progress
   bar assumes the game has crashed.
   --------------------------------------------------------------------------- */

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0..1. Falls back to 0 while the total length is unknown. */
  fraction: number;
  /** Bytes per second, averaged over the whole transfer so far. */
  bytesPerSecond: number;
  /** Seconds remaining, or null while it can't be estimated. */
  secondsRemaining: number | null;
}

export type UploadErrorKind = "network" | "http" | "abort" | "timeout";

export class UploadError extends Error {
  readonly kind: UploadErrorKind;
  readonly status: number | null;
  readonly responseBody: string | null;

  constructor(
    message: string,
    kind: UploadErrorKind,
    status: number | null = null,
    responseBody: string | null = null,
  ) {
    super(message);
    this.name = "UploadError";
    this.kind = kind;
    this.status = status;
    this.responseBody = responseBody;
  }
}

export interface UploadOptions {
  /** Abort the transfer (player left the lobby, round moved on). */
  signal?: AbortSignal;
  /**
   * Content-Type sent with the PUT. Must match whatever the presign was signed
   * with, or R2 rejects the request with a signature mismatch.
   */
  contentType?: string;
  /** Extra signed headers, if the presign required any. */
  headers?: Record<string, string>;
  /** Give up after this long with no activity. Default 0 (no timeout). */
  timeoutMs?: number;
}

export interface UploadResult {
  status: number;
  /** R2 returns an ETag; useful for de-duping retries. Null if not exposed. */
  etag: string | null;
  bytes: number;
  durationMs: number;
}

function describeHttpFailure(status: number, body: string): string {
  if (status === 403) {
    return "The upload link expired or was rejected. Try uploading again.";
  }
  if (status === 400) {
    return "Storage rejected the upload as malformed. Try uploading again.";
  }
  if (status === 413) {
    return "The file is too large for storage to accept.";
  }
  if (status >= 500) {
    return "Storage is having a moment. Try uploading again.";
  }
  const detail = body.trim().slice(0, 200);
  return detail
    ? `Upload failed (HTTP ${status}): ${detail}`
    : `Upload failed with HTTP ${status}.`;
}

/**
 * PUT a blob to a presigned R2 URL, reporting progress as it goes.
 *
 * Rejects with an `UploadError` carrying the real status and body — never a
 * bare "failed to fetch".
 */
export function uploadToR2(
  file: Blob,
  presignedUrl: string,
  onProgress?: (progress: UploadProgress) => void,
  options: UploadOptions = {},
): Promise<UploadResult> {
  const { signal, contentType, headers, timeoutMs = 0 } = options;

  return new Promise<UploadResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadError("Upload cancelled.", "abort"));
      return;
    }

    const startedAt = performance.now();
    const xhr = new XMLHttpRequest();
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
      xhr.upload.onprogress = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.ontimeout = null;
      xhr.onabort = null;
    };

    const failWith = (error: UploadError) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const onAbort = () => {
      if (settled) return;
      try {
        xhr.abort();
      } catch {
        /* Already finished. */
      }
      failWith(new UploadError("Upload cancelled.", "abort"));
    };

    xhr.open("PUT", presignedUrl, true);
    xhr.responseType = "text";
    if (timeoutMs > 0) xhr.timeout = timeoutMs;

    const type = contentType ?? file.type ?? "";
    if (type) xhr.setRequestHeader("Content-Type", type);
    for (const [key, value] of Object.entries(headers ?? {})) {
      xhr.setRequestHeader(key, value);
    }

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        const total = event.lengthComputable ? event.total : file.size;
        const elapsedSec = Math.max(1e-3, (performance.now() - startedAt) / 1000);
        const bytesPerSecond = event.loaded / elapsedSec;
        const remainingBytes = Math.max(0, total - event.loaded);
        onProgress({
          loaded: event.loaded,
          total,
          fraction: total > 0 ? Math.min(1, event.loaded / total) : 0,
          bytesPerSecond,
          secondsRemaining: bytesPerSecond > 0 ? remainingBytes / bytesPerSecond : null,
        });
      };
    }

    xhr.onload = () => {
      if (settled) return;
      const status = xhr.status;
      if (status >= 200 && status < 300) {
        settled = true;
        cleanup();
        onProgress?.({
          loaded: file.size,
          total: file.size,
          fraction: 1,
          bytesPerSecond: file.size / Math.max(1e-3, (performance.now() - startedAt) / 1000),
          secondsRemaining: 0,
        });
        resolve({
          status,
          etag: xhr.getResponseHeader("ETag"),
          bytes: file.size,
          durationMs: performance.now() - startedAt,
        });
        return;
      }
      const body = typeof xhr.response === "string" ? xhr.response : "";
      failWith(new UploadError(describeHttpFailure(status, body), "http", status, body || null));
    };

    xhr.onerror = () =>
      failWith(
        new UploadError(
          "The upload couldn't reach storage. Check your connection and try again.",
          "network",
        ),
      );

    xhr.ontimeout = () =>
      failWith(new UploadError("The upload timed out. Try again on a better connection.", "timeout"));

    xhr.onabort = () => failWith(new UploadError("Upload cancelled.", "abort"));

    signal?.addEventListener("abort", onAbort);

    try {
      xhr.send(file);
    } catch (err) {
      failWith(
        new UploadError(
          err instanceof Error ? err.message : "The upload could not be started.",
          "network",
        ),
      );
    }
  });
}

/**
 * Upload with retries on transient failures (network blips, 5xx).
 * Never retries a 4xx — an expired presign needs a fresh URL, not another try.
 */
export async function uploadToR2WithRetry(
  file: Blob,
  presignedUrl: string,
  onProgress?: (progress: UploadProgress) => void,
  options: UploadOptions & { attempts?: number; retryDelayMs?: number } = {},
): Promise<UploadResult> {
  const { attempts = 3, retryDelayMs = 800, ...uploadOptions } = options;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await uploadToR2(file, presignedUrl, onProgress, uploadOptions);
    } catch (err) {
      lastError = err;
      const retryable =
        err instanceof UploadError &&
        (err.kind === "network" || err.kind === "timeout" || (err.status ?? 0) >= 500);
      if (!retryable || attempt === attempts - 1 || uploadOptions.signal?.aborted) throw err;
      await new Promise((resolve) => window.setTimeout(resolve, retryDelayMs * (attempt + 1)));
    }
  }

  throw lastError;
}
